import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import packageJson from '../../../package.json';
import { refreshSession } from '../../actions/authentication';

const POLL_MS = 5 * 60 * 1000;

// Records the mismatch the user last refreshed for. A refresh can only fix a mismatch
// caused by a stale bundle; it cannot fix one caused by the *server* being out of step
// (a meta.json that was never regenerated, say). If we come back from the reload looking
// at the exact same mismatch, the refresh did not help and re-offering it just traps the
// user behind a button they cannot clear - so we stay quiet until the mismatch changes.
const REFRESH_GUARD_KEY = 'newVersionRefreshedFor';

const readGuard = () => {
  try {
    return sessionStorage.getItem(REFRESH_GUARD_KEY);
  } catch {
    return null; // private mode / storage disabled
  }
};

const writeGuard = (signature) => {
  try {
    sessionStorage.setItem(REFRESH_GUARD_KEY, signature);
  } catch { /* private mode / storage disabled */ }
};

const clearGuard = () => {
  try {
    sessionStorage.removeItem(REFRESH_GUARD_KEY);
  } catch { /* private mode / storage disabled */ }
};

// Any change counts, not just a higher number: a rollback leaves the browser running a
// bundle the deployed server no longer expects, which needs the same reload a release does.
const versionsDiffer = (a, b) => {
  if (a == null || b == null) return false;
  const left = String(a).trim();
  const right = String(b).trim();
  if (left === '' || right === '') return false;
  return left !== right;
};

export default function NewVersionNotice() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [available, setAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);
  // The exact mismatch currently being offered, so the click can record what it tried.
  const signatureRef = useRef(null);
  // The sopkoa version baked into the token this session is running on. Read live from
  // redux so any path that mints a fresh token (login, tenant switch, refreshSession)
  // resets the baseline instead of leaving the button stuck on.
  const tokenServerVersion = useSelector(state => state.auth?.user?.serverVersion);
  const serverVersionRef = useRef(tokenServerVersion);
  serverVersionRef.current = tokenServerVersion;

  useEffect(() => {
    let cancelled = false;
    let id = null;

    const readJson = async (url) => {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return null;
      // A missing file does not 404 here - the SPA fallback answers with index.html and a
      // 200 - so check the type. Otherwise the JSON parse error vanishes into the catch
      // below and the version check silently stops working.
      const type = res.headers.get('content-type') || '';
      if (!type.includes('json')) {
        console.warn(`${url} did not return JSON (${type || 'no content-type'}). Version check skipped - did this build run generate-build-version?`);
        return null;
      }
      return res.json();
    };

    const check = async () => {
      let signature = null;
      try {
        const meta = await readJson(`/meta.json?_=${Date.now()}`);
        if (meta != null && versionsDiffer(meta.version, packageJson.version)) {
          signature = `sopui ${packageJson.version} -> ${meta.version}`;
        }
      } catch { /* network blip — try again next interval */ }

      if (signature == null) {
        try {
          const env = await readJson(`/users/env?_=${Date.now()}`);
          const baseline = serverVersionRef.current;
          if (env != null && versionsDiffer(env.version, baseline)) {
            signature = `sopkoa ${baseline} -> ${env.version}`;
          }
        } catch { /* network blip — try again next interval */ }
      }

      if (cancelled) return;

      if (signature == null) {
        // Everything lines up - forget any refresh we previously gave up on, so the next
        // real deployment gets offered cleanly.
        clearGuard();
        return;
      }

      if (readGuard() === signature) {
        console.warn(`Still seeing (${signature}) after refreshing for it. Not offering again.`);
        if (id != null) clearInterval(id);
        return;
      }

      console.info(`Version change detected (${signature}). Offering refresh.`);
      signatureRef.current = signature;
      setAvailable(true);
      if (id != null) clearInterval(id);
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };

    id = setInterval(check, POLL_MS);
    document.addEventListener('visibilitychange', onVisible);
    check();

    return () => {
      cancelled = true;
      if (id != null) clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const handleUpdate = () => {
    if (updating) return;
    setUpdating(true);
    // Remember what we are refreshing for. If the reload lands on the same mismatch the
    // refresh could not fix it, and the check above will stop offering it.
    if (signatureRef.current != null) writeGuard(signatureRef.current);
    dispatch(refreshSession());
  };

  if (!available) return null;

  return (
    <Tooltip title={t('newVersionTooltip', 'A different version is available. Click to refresh.')}>
      <span>
        <Button
          size="small"
          color="warning"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleUpdate}
          disabled={updating}
          sx={{ mr: 1 }}
        >
          {t('newVersionAvailable', 'Update available')}
        </Button>
      </span>
    </Tooltip>
  );
}
